/**
 * Validación EXHAUSTIVA de una fila del Excel de importación de expedientes.
 *
 * Es la ÚNICA fuente de verdad de las reglas: la usa el frontend para bloquear
 * el fichero antes de empezar a importar, y el backend en `POST /expedients/add`
 * como puerta de entrada real (por si alguien llama a la API directamente).
 *
 * Es pura: no toca BD ni red. Las validaciones que necesitan base de datos
 * (que el `user_id` exista, que los operadores sean de esa company) se quedan
 * en el backend, después de esta.
 *
 * Devuelve SIEMPRE la lista completa de errores de la fila (no corta en el
 * primero) para que el usuario pueda arreglar el Excel de una pasada.
 */

const { normalizeExpedientType } = require('../helpers/expedient-type');
const { parsePhoneColumns } = require('../helpers/phone-parsing');
const { isValidCurrency, CURRENCY_CODES } = require('../constants/currencies');

/** Columnas obligatorias, con la etiqueta que se enseña al usuario. */
const REQUIRED_COLUMNS = [
  ['user_id', 'ID del cliente'],
  ['client_internal_reference', 'Referencia interna'],
  ['tipo_deuda', 'Tipo de deuda'],
  ['deudor', 'Nombre del deudor'],
  ['mail_deudor', 'Email del deudor'],
  ['domicilio_deudor', 'Domicilio del deudor'],
  ['ciudad_deudor', 'Ciudad del deudor'],
  ['cp_deudor', 'Código postal del deudor'],
];

/** Columnas numéricas opcionales: si vienen, tienen que ser un número. */
const OPTIONAL_NUMERIC_COLUMNS = [
  'presupuesto',
  'porcentaje_presupuesto',
  'debt_activity_amount',
];

/** Columnas de fecha: si vienen, tienen que ser una fecha reconocible. */
const DATE_COLUMNS = [
  'fecha_fin_reclamacion_extrajudicial',
  'debt_activity_date',
  'detalle_deuda_fecha_impago',
  'detalle_deuda_fecha_vencimiento',
];

const isBlank = value => value == null || String(value).trim() === '';

/** ObjectId de Mongo: 24 caracteres hexadecimales. */
const isObjectIdLike = value => /^[a-f\d]{24}$/i.test(String(value ?? '').trim());

/**
 * Parsea una fecha del Excel: serial numérico, `dd/mm/yyyy` o algo que Date
 * entienda. Devuelve `null` si no es una fecha válida.
 *
 * Debe seguir el mismo criterio que el backend al persistir; si aquí decimos que
 * vale y allí falla, el usuario ve un 500 en vez de un error de validación.
 */
function parseImportDate(value) {
  if (isBlank(value)) return null;

  if (typeof value === 'number') {
    // Serial de Excel. El origen es el 30/12/1899: Excel arrastra el 29/02/1900,
    // que no existió, así que contar desde el 1 de enero suma un día. Aquí solo
    // decide si la fecha es válida, pero tiene que dar lo mismo que el backend.
    const date = new Date(Date.UTC(1899, 11, 30) + value * 86400000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(value).trim();
  const parts = text.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const date = new Date(`${year}-${month}-${day}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Email: comprobación de forma, no de existencia. */
const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value ?? '').trim());

/**
 * Un número de teléfono es aceptable si, ya separado del prefijo, tiene entre 6
 * y 15 dígitos. NO se exige que sea móvil: un deudor puede tener fijo.
 */
const isValidPhoneNumber = number => /^\d{6,15}$/.test(String(number ?? ''));

/**
 * Valida una fila del Excel.
 *
 * @param {Object} row - Fila tal cual sale de `sheet_to_json` (claves = cabeceras).
 * @returns {Array<{column: string, message: string}>} errores (vacío si es válida).
 */
function validateImportRow(row = {}) {
  const errors = [];
  const add = (column, message) => errors.push({ column, message });

  // --- Obligatorios ---
  REQUIRED_COLUMNS.forEach(([column, label]) => {
    if (isBlank(row[column])) add(column, `${label}: obligatorio y está vacío`);
  });

  if (!isBlank(row.user_id) && !isObjectIdLike(row.user_id)) {
    add('user_id', 'ID del cliente: no tiene forma de identificador válido (24 caracteres hex)');
  }

  if (!isBlank(row.mail_deudor) && !isValidEmail(row.mail_deudor)) {
    add('mail_deudor', `Email del deudor: "${row.mail_deudor}" no es un email válido`);
  }

  // --- Importe de la deuda ---
  if (isBlank(row.importe_deuda)) {
    add('importe_deuda', 'Importe de la deuda: obligatorio y está vacío');
  } else {
    const amount = Number(row.importe_deuda);
    if (!Number.isFinite(amount)) {
      add('importe_deuda', `Importe de la deuda: "${row.importe_deuda}" no es un número`);
    } else if (amount <= 0) {
      add('importe_deuda', 'Importe de la deuda: tiene que ser mayor que 0');
    }
  }

  // --- Teléfonos ---
  const { phones, error: phonesError } = parsePhoneColumns(
    row.telefono_deudor,
    row.country_prefix
  );
  if (phonesError) {
    add('telefono_deudor', phonesError);
  } else if (phones.length === 0) {
    add('telefono_deudor', 'Teléfono del deudor: obligatorio, hace falta al menos uno');
  } else {
    phones.forEach(phone => {
      if (!isValidPhoneNumber(phone.number)) {
        add('telefono_deudor', `Teléfono "${phone.number}": debe tener entre 6 y 15 dígitos`);
      }
    });
  }

  // --- Divisa (opcional; por defecto EUR) ---
  if (!isBlank(row.divisa) && !isValidCurrency(row.divisa)) {
    add(
      'divisa',
      `Divisa "${row.divisa}" no soportada. Admitidas: ${CURRENCY_CODES.join(', ')}`
    );
  }

  // --- Tipo de expediente (opcional; vacío = el de la empresa) ---
  const rawType = row.tipo_expediente ?? row.type ?? row.expedient_type;
  if (!isBlank(rawType) && !normalizeExpedientType(rawType)) {
    add(
      'tipo_expediente',
      `Tipo de expediente "${rawType}" no reconocido. Admitidos: account_receivable, judicial, extra_judicial`
    );
  }

  // --- Numéricos opcionales ---
  OPTIONAL_NUMERIC_COLUMNS.forEach(column => {
    if (!isBlank(row[column]) && !Number.isFinite(Number(row[column]))) {
      add(column, `${column}: "${row[column]}" no es un número`);
    }
  });

  // --- Fechas ---
  DATE_COLUMNS.forEach(column => {
    if (!isBlank(row[column]) && !parseImportDate(row[column])) {
      add(column, `${column}: "${row[column]}" no es una fecha válida (usa dd/mm/aaaa)`);
    }
  });

  return errors;
}

/**
 * Campos del Excel que identifican al deudor, con la etiqueta que se enseña.
 *
 * Sirven para decidir si dos filas con la misma referencia son el mismo deudor
 * con varias facturas (lo normal) o dos deudores distintos (un error real).
 */
const DEBTOR_IDENTITY_COLUMNS = [
  ['nif', 'NIF/CIF del deudor'],
  ['mail_deudor', 'Email del deudor'],
  ['deudor', 'Nombre del deudor'],
];

/**
 * Normaliza un valor de identidad para poder compararlo: sin mayúsculas ni
 * espacios de más y, en el NIF, sin nada que no sea letra o número, porque cada
 * cliente lo escribe a su manera (`13158253E-` es el mismo que `13158253E`).
 */
function normalizeIdentity(column, value) {
  const text = String(value ?? '').trim().toLowerCase();
  if (column === 'nif') return text.replace(/[^a-z0-9]/g, '');
  return text.replace(/\s+/g, ' ');
}

/**
 * Compara la identidad del deudor de dos filas y devuelve los campos en los que
 * discrepan.
 *
 * Solo se comparan los campos que vienen rellenos en LAS DOS filas: que la
 * segunda no repita el NIF no es una contradicción, es que no lo trae.
 *
 * @returns {Array<{column:string, label:string, a:string, b:string}>}
 */
function debtorIdentityConflicts(rowA = {}, rowB = {}) {
  return DEBTOR_IDENTITY_COLUMNS.reduce((conflicts, [column, label]) => {
    if (isBlank(rowA[column]) || isBlank(rowB[column])) return conflicts;
    if (normalizeIdentity(column, rowA[column]) === normalizeIdentity(column, rowB[column])) {
      return conflicts;
    }
    conflicts.push({
      column,
      label,
      a: String(rowA[column]).trim(),
      b: String(rowB[column]).trim(),
    });
    return conflicts;
  }, []);
}

/**
 * Valida el fichero entero: cada fila más las reglas que solo se ven mirando el
 * conjunto (referencias repetidas).
 *
 * Dos filas del mismo cliente con la misma referencia interna NO crean dos
 * expedientes: el backend trata la segunda como acumulación y le añade la deuda
 * al expediente que ya existe (`addDebtActivityItem` recalcula `importe_deuda`).
 *
 * **Eso es lo normal, no un fallo.** En casi todas las carteras la referencia
 * interna ES el CIF del deudor, así que un deudor con tres facturas son tres
 * filas con el mismo valor y acaban en un expediente con tres deudas.
 *
 * Lo que sí es un error es repetir la referencia para deudores DISTINTOS: ahí la
 * deuda de uno acaba en el expediente del otro y, como al acumular no se
 * actualizan los datos del deudor, los de la segunda fila se pierden en
 * silencio. Por eso la regla mira la identidad (`nif`, `mail_deudor`, `deudor`)
 * y solo bloquea cuando discrepa.
 *
 * @param {Array<Object>} rows
 * @returns {{ valid: boolean, rows: Array<{index:number, line:number, errors:Array}>, totalErrors:number }}
 *   `line` es el número de fila del Excel (1 = cabeceras), listo para enseñar.
 */
function validateImportFile(rows = []) {
  const perRow = rows.map((row, index) => ({
    index,
    line: index + 2,
    errors: validateImportRow(row),
  }));

  // Referencia interna repetida dentro del mismo cliente: solo es error si las
  // filas que la comparten son deudores distintos (ver comentario de arriba).
  const seen = new Map();
  rows.forEach((row, index) => {
    if (isBlank(row?.client_internal_reference) || isBlank(row?.user_id)) return;
    const key = `${String(row.user_id).trim()}::${String(row.client_internal_reference).trim().toLowerCase()}`;

    const firstIndex = seen.get(key);
    if (firstIndex === undefined) {
      seen.set(key, index);
      return;
    }

    const conflicts = debtorIdentityConflicts(rows[firstIndex], row);
    if (conflicts.length === 0) return; // Mismo deudor, varias facturas: correcto.

    const detail = conflicts.map(c => `${c.label}: "${c.b}" frente a "${c.a}"`).join('; ');
    perRow[index].errors.push({
      column: 'client_internal_reference',
      message:
        `Referencia "${row.client_internal_reference}" repetida para el mismo cliente ` +
        `(ya está en la fila ${firstIndex + 2}) pero con otro deudor — ${detail}. ` +
        `Las dos filas acabarían en el mismo expediente: si es el mismo deudor, unifica ` +
        `sus datos; si son distintos, dales referencias distintas.`,
    });
  });

  const invalid = perRow.filter(r => r.errors.length > 0);
  return {
    valid: invalid.length === 0,
    rows: invalid,
    totalErrors: invalid.reduce((sum, r) => sum + r.errors.length, 0),
  };
}

module.exports = {
  validateImportRow,
  validateImportFile,
  debtorIdentityConflicts,
  parseImportDate,
  REQUIRED_COLUMNS,
  DATE_COLUMNS,
  DEBTOR_IDENTITY_COLUMNS,
};
